import { useEffect, useRef, useState } from "react";
import { Bike, Plane, Ship, Truck, Zap } from "lucide-react";

const SCENES = [
  {
    id: "pickup", step: "01", icon: Bike, title: "Doorstep Pickup",
    desc: "An IndiaFast rider collects your parcel from your doorstep — packed, sealed and scanned into the network.",
    alt: "IndiaFast pickup rider on a motorbike with a branded orange delivery box",
    chip: "AWB IFAST1000001 · Picked up",
    img: "/videos/bike_poster.jpg",
    video: "/videos/bike.mp4",
    videoWebm: "/videos/bike.webm",
  },
  {
    id: "road", step: "02", icon: Truck, title: "Surface Express",
    desc: "GPS-tracked IndiaFast trucks carry your shipment across 29,000+ Indian pincodes to the nearest international gateway.",
    alt: "Convoy of IndiaFast branded trucks driving on a highway at sunrise",
    chip: "AWB IFAST1000001 · Linehaul DEL → BOM",
    img: "/videos/road_poster.jpg",
    video: "/videos/road.mp4",
    videoWebm: "/videos/road.webm",
  },
  {
    id: "air", step: "03", icon: Plane, title: "Air Cargo",
    desc: "At the cargo terminal, your shipment is loaded onto IndiaFast air freight — priority connections to 220+ countries.",
    alt: "IndiaFast branded cargo airplane being loaded at an airport cargo terminal",
    chip: "AWB IFAST1000001 · Departed BOM",
    img: "/videos/air_poster.jpg",
    video: "/videos/air.mp4",
    videoWebm: "/videos/air.webm",
  },
  {
    id: "sea", step: "04", icon: Ship, title: "Ocean Freight",
    desc: "For heavy commercial cargo, IndiaFast ocean lines move containers worldwide — FCL and LCL, fully customs-guided.",
    alt: "IndiaFast branded container ship sailing on the ocean",
    chip: "AWB IFAST1000001 · Vessel departed Nhava Sheva",
    img: "/videos/sea_poster.jpg",
    video: "/videos/sea.mp4",
    videoWebm: "/videos/sea.webm",
  },
];

const RIBBON = ["INDIAFAST EXPRESS", "DOOR-TO-DOOR", "220+ COUNTRIES", "LIVE TRACKING", "29,000+ PINCODES", "CUSTOMS GUIDED"];
const SCENE_MS = 5000;

function BrandRibbon() {
  const row = [...RIBBON, ...RIBBON];
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 overflow-hidden border-t border-white/10 bg-navy/60 py-2.5 backdrop-blur-sm" aria-hidden="true">
      <div className="flex w-max items-center gap-8" style={{ animation: "marquee 26s linear infinite" }}>
        {row.map((t, i) => (
          <span key={i} className="flex shrink-0 items-center gap-2.5 text-[10px] font-bold uppercase tracking-[0.25em] text-white/70">
            <Zap className="h-3 w-3 text-saffron" fill="currentColor" /> {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function Caption({ scene }) {
  return (
    <div key={scene.id} className="animate-fade-up" data-testid="story-scene-caption">
      <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-saffron">
        <span>{scene.step}</span>
        <span className="h-px w-10 bg-saffron/60" />
        <span>How your shipment travels</span>
      </p>
      <h3 className="mt-3 font-heading text-3xl font-medium tracking-tight text-white sm:text-5xl">
        {scene.title}
      </h3>
      <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-200 sm:text-base">{scene.desc}</p>
    </div>
  );
}

function SceneOverlay({ scene, active }) {
  return (
    <>
      <div className={`floaty absolute left-4 top-20 flex items-center gap-2.5 rounded-2xl border border-white/20 bg-navy/50 px-4 py-2.5 backdrop-blur-xl transition-opacity duration-700 sm:left-8 ${active ? "opacity-100" : "opacity-0"}`}
           data-testid={`story-badge-${scene.id}`}>
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-saffron text-white shadow-lg shadow-saffron/40">
          <Zap className="h-4 w-4" fill="currentColor" />
        </span>
        <div>
          <p className="font-heading text-sm font-extrabold tracking-tight text-white">INDIA<span className="text-saffron">FAST</span></p>
          <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/60">{scene.title}</p>
        </div>
        <scene.icon className="ml-2 h-4 w-4 text-saffron" />
      </div>

      <svg className={`pointer-events-none absolute inset-x-0 top-1/3 h-40 w-full transition-opacity duration-700 ${active ? "opacity-60" : "opacity-0"}`}
           viewBox="0 0 100 20" preserveAspectRatio="none" aria-hidden="true">
        <path d="M-2,16 C20,4 38,14 58,8 S86,4 102,6" fill="none" stroke="#FF5A1F" strokeWidth="0.35"
              strokeDasharray="1.4 1.4" className="route-dash" strokeLinecap="round" />
        <path d="M-2,18 C22,10 40,18 62,12 S88,8 102,10" fill="none" stroke="#ffffff" strokeOpacity="0.35" strokeWidth="0.25"
              strokeDasharray="1 1.6" className="route-dash-slow" strokeLinecap="round" />
      </svg>

      <div className={`floaty-slow absolute right-4 top-1/4 hidden rounded-xl border border-white/20 bg-white/10 px-3.5 py-2 backdrop-blur-xl transition-opacity duration-700 sm:right-10 md:block ${active ? "opacity-100" : "opacity-0"}`}
           data-testid={`story-chip-${scene.id}`}>
        <p className="font-mono text-[10px] text-slate-200">{scene.chip.split("·")[0].trim()}</p>
        <p className="text-[10px] font-bold text-saffron">{scene.chip.split("·")[1]?.trim()}</p>
      </div>
    </>
  );
}

export default function ScrollStory() {
  const [idx, setIdx] = useState(0);
  const [reduced, setReduced] = useState(false);
  const videoRefs = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    setReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  useEffect(() => {
    if (reduced) return undefined;
    timerRef.current = setInterval(() => setIdx((i) => (i + 1) % SCENES.length), SCENE_MS);
    return () => clearInterval(timerRef.current);
  }, [reduced]);

  useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === idx) {
        v.play().catch(() => {});
      } else {
        v.pause();
      }
    });
  }, [idx, reduced]);

  const jump = (i) => {
    setIdx(i);
    clearInterval(timerRef.current);
    if (!reduced) timerRef.current = setInterval(() => setIdx((x) => (x + 1) % SCENES.length), SCENE_MS);
  };

  if (reduced) {
    return (
      <section className="bg-navy" data-testid="scroll-story">
        {SCENES.map((s) => (
          <div key={s.id} className="relative">
            <img src={s.img} alt={s.alt} loading="lazy" className="h-[60vh] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/20 to-transparent" />
            <div className="absolute bottom-0 left-0 p-6 sm:p-10"><Caption scene={s} /></div>
          </div>
        ))}
      </section>
    );
  }

  return (
    <section className="relative h-[88vh] min-h-[560px] overflow-hidden bg-navy" data-testid="scroll-story">
      {SCENES.map((s, i) => (
        <div key={s.id} data-testid={`story-scene-${s.id}`} aria-hidden={i !== idx}
             className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}>
          <video
            ref={(el) => { videoRefs.current[i] = el; }}
            poster={s.img}
            muted
            loop
            playsInline
            autoPlay={i === 0}
            preload={i === 0 ? "auto" : "metadata"}
            aria-label={s.alt}
            data-testid={`story-video-${s.id}`}
            className={`h-full w-full object-cover transition-transform duration-[1400ms] ${i === idx ? "scale-105" : "scale-100"}`}
          >
            <source src={s.video} type="video/mp4" />
            <source src={s.videoWebm} type="video/webm" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/15 to-navy/30" />
          <SceneOverlay scene={s} active={i === idx} />
        </div>
      ))}

      <div className="absolute bottom-0 left-0 right-0 mx-auto flex w-full max-w-7xl items-end justify-between gap-8 px-4 pb-16 sm:px-6">
        <div className="min-h-[11rem] sm:min-h-[13rem]">
          <Caption scene={SCENES[idx]} />
        </div>
        <div className="hidden flex-col items-center gap-2.5 sm:flex" data-testid="story-progress">
          {SCENES.map((s, i) => (
            <button key={s.id} onClick={() => jump(i)} data-testid={`story-dot-${i}`}
                    aria-label={`Show ${s.title}`}
                    className={`rounded-full transition-all duration-500 ${i === idx ? "h-8 w-2.5 bg-saffron" : "h-2.5 w-2.5 bg-white/30 hover:bg-white/60"}`} />
          ))}
        </div>
      </div>

      <div className="absolute left-1/2 top-8 hidden -translate-x-1/2 items-center gap-3 rounded-full border border-white/20 bg-navy/40 px-5 py-2 backdrop-blur-xl sm:flex">
        {SCENES.map((s, i) => (
          <button key={s.id} onClick={() => jump(i)} data-testid={`story-stepper-${s.id}`}
                  className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${i === idx ? "text-saffron" : "text-white/50 hover:text-white"}`}>
            <s.icon className="h-3.5 w-3.5" /> {s.title}
          </button>
        ))}
      </div>

      <BrandRibbon />
    </section>
  );
}
